/**
 * Наблюдения — records whose published figures do not reconcile.
 *
 * Nothing here is hidden and nothing is corrected. A reader who finds a
 * total that looks wrong should be able to come here and see which records
 * were doubtful and why, rather than take on trust that the number was
 * cleaned. Only one category is left out of the site's totals, and the page
 * says which and why.
 */

import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { graphql } from '../gql';
import { request } from '../lib/graphql';
import { DataTable, Pager } from '../components/DataTable';
import { Amount,
  Badge, ErrorState, Link, Loading, PageHead, SectionHead, Stat,
} from '../components/ui';
import { date, num, truncate } from '../lib/format';

const PAGE_SIZE = 25;

const COUNTS = graphql(`
  query ObservationCounts {
    valueObservationCounts { value count }
    dataIssueCounts { value count }
    registerCoverage {
      companies known pending outsideRegister
      beneficialOwners encumbrances maskedParties checkedThrough
    }
  }
`);

const OBSERVATIONS = graphql(`
  query ValueObservations($limit: Int!, $offset: Int!, $flag: String) {
    valueObservations(limit: $limit, offset: $offset, flag: $flag) {
      tenderId
      title
      buyer
      publicationDate
      estimatedValue
      awardedAmount
      currency
      ratio
      flag
      reason
      excluded
      lotCount
      winnerCount
    }
  }
`);

const ISSUES = graphql(`
  query DataIssues($limit: Int!) {
    dataIssues(limit: $limit) { source recordId value reason lastSeen }
  }
`);

interface CoverageData {
  companies: number; known: number; pending: number; outsideRegister: number;
  beneficialOwners: number; encumbrances: number; maskedParties: number;
  checkedThrough?: string | null;
}

/* How much of the ownership picture exists yet.
 *
 * A company page showing no owner is ambiguous in a way that misleads: it
 * can mean the register records none, or that the record has not been
 * fetched yet. Only this page can tell the two apart, so the counts are
 * published rather than left to whoever runs the pipeline. */
function Coverage({ data }: Readonly<{ data: CoverageData }>) {
  const share = data.companies
    ? Math.round((data.known / data.companies) * 100) : 0;
  return (
    <section className="block">
      <SectionHead
        title="Какво знаем за собствеността"
        aside={data.checkedThrough
          ? `проверено до ${date(data.checkedThrough)}` : undefined}
      />
      <p className="page-lede">
        {'Собствеността се чете от два източника с различен обхват. Местното '
         + 'копие на Търговския регистър отговаря наведнъж, но архивът му '
         + 'започва от 2021 г.; порталът на Агенцията по вписванията стига до '
         + '2008 г., но отговаря за по едно дружество и с ограничение колко '
         + 'често може да бъде питан. Затова липсващият собственик на дадена '
         + 'страница означава едно от двете — регистърът не вписва такъв, или '
         + 'записът още не е изтеглен. Числата по-долу казват кое колко е.'}
      </p>
      <div className="grid grid-4">
        <Stat value={`${num(data.known)} (${share}%)`} label="С установени лица" />
        <Stat value={num(data.pending)} label="Чакат портала" />
        <Stat value={num(data.outsideRegister)} label="Извън Търговския регистър" />
        <Stat value={num(data.maskedParties)} label="Със скрита самоличност" />
      </div>
      <p className="muted" style={{ marginTop: '0.9rem' }}>
        {`От ${num(data.companies)} дружества в обхвата на публичните средства. `}
        {`Досега са установени ${num(data.beneficialOwners)} действителни собственици `}
        {`и ${num(data.encumbrances)} тежести (запори, описания и залози). `}
        {'„Извън Търговския регистър“ са общини, министерства, училища и '
         + 'сдружения — те се вписват в БУЛСТАТ и се чакат оттам, не от портала. '
         + '„Със скрита самоличност“ са лица, които регистърът назовава по име, '
         + 'но без идентификатор; те остават несвързани нарочно, защото '
         + 'заместителят, който регистърът публикува, е един и същ за всички и '
         + 'свързването по него би слепило различни хора в един.'}
      </p>
    </section>
  );
}

/* What each category means, and whether it changes the totals. Written out
   because the categories are not equally serious and treating them alike
   would be its own distortion. */
const CATEGORIES: Record<string, { label: string; excluded: boolean; note: string }> = {
  'duplicate-publication': {
    label: 'Публикувана два пъти',
    excluded: true,
    note: 'Договор над европейския праг се обявява в ЦАИС ЕОП и отново в TED. Двата '
      + 'записа не си приличат — различни номера, а TED носи заглавието на обявлението '
      + 'вместо предмета, който възложителят е написал — затова досега влизаха в графа '
      + 'като две отделни поръчки. Обявлението в TED се разпознава по съвпадение на '
      + 'публикуваната стойност, датата и CPV кода, при задължително съвпадение и на '
      + 'възложителя, и се свързва с националния запис. Публикуваните числа са запазени '
      + 'както са; изважда се само стойността за сравнение, за да не се брои един договор '
      + 'два пъти. Обявление, което отговаря на повече от един национален запис, не се '
      + 'свързва — сгрешено сливане е по-лошо от видимо повторение.',
  },
  'decimal-shift': {
    label: 'Изпусната десетична запетая',
    excluded: true,
    note: 'Договорената стойност е точно 100 или 1000 пъти прогнозната. Българските суми '
      + 'се публикуват с два знака след запетаята, така че сума без разделител е точно сто '
      + 'пъти по-голяма. Не може да се определи кое от двете числа е вярното — прогнозата '
      + 'може да е сгрешената — затова тези поръчки не влизат в никоя обща сума в сайта. '
      + 'Публикуваните числа са запазени както са.',
  },
  'award-above-estimate': {
    label: 'Договор над прогнозата',
    excluded: false,
    note: 'Договорената стойност е над 10 пъти прогнозната. Обичайно рамково споразумение '
      + 'или прогноза за единична цена срещу договор за обща сума. Договорената стойност е '
      + 'тази, която е подписана, затова се брои — ненадеждно е само сравнението с '
      + 'прогнозата.',
  },
  'award-below-estimate': {
    label: 'Договор под прогнозата',
    excluded: false,
    note: 'Договорената стойност е под 2% от прогнозната. Обичайно единична цена срещу '
      + 'обща прогноза, или стойност на една обособена позиция, записана срещу цялото '
      + 'обявление. Стойността се брои — ненадеждно е сравнението.',
  },
};

export function ObservationsPage() {
  const [flag, setFlag] = useState<string>('');
  const [page, setPage] = useState(0);

  const counts = useQuery({ queryKey: ['obs-counts'], queryFn: () => request(COUNTS) });
  const rows = useQuery({
    queryKey: ['obs', flag, page],
    queryFn: () => request(OBSERVATIONS, {
      limit: PAGE_SIZE, offset: page * PAGE_SIZE, flag: flag || null,
    }),
    placeholderData: keepPreviousData,
  });
  const issues = useQuery({ queryKey: ['issues'], queryFn: () => request(ISSUES, { limit: 40 }) });

  const byFlag = Object.fromEntries(
    (counts.data?.valueObservationCounts ?? []).map((row) => [row.value, row.count]));
  const total = flag
    ? (byFlag[flag] ?? 0)
    : Object.values(byFlag).reduce((sum, n) => sum + n, 0);
  const excludedCount = byFlag['decimal-shift'] ?? 0;
  const issueTotal = (counts.data?.dataIssueCounts ?? [])
    .reduce((sum, row) => sum + row.count, 0);
  const coverage = counts.data?.registerCoverage;

  return (
    <>
      <PageHead
        eyebrow="Наблюдения"
        title="Данни, които не се връзват"
        lede={'Поръчки, чиито публикувани числа си противоречат, и записи, които изобщо не '
          + 'можаха да бъдат използвани. Нищо тук не е скрито и нищо не е поправено: '
          + 'числата са показани както са публикувани, а до всяко стои причината, поради '
          + 'която е отбелязано. Само една категория остава извън сумите в сайта.'}
      />

      {counts.isError && <ErrorState message={(counts.error).message} />}
      <div className="grid grid-4">
        <Stat value={num(total)} label="Поръчки с разминаване" />
        <Stat value={num(excludedCount)} label="Извън общите суми" />
        <Stat value={num(issueTotal)} label="Неизползваеми записи" />
      </div>

      {coverage && <Coverage data={coverage} />}

      <div className="facet-row">
        <label className="facet-label" htmlFor="obs-flag">Категория</label>
        <select
          className="facet-select"
          id="obs-flag"
          value={flag}
          onChange={(event) => { setFlag(event.target.value); setPage(0); }}
        >
          <option value="">всички ({num(total)})</option>
          {Object.entries(CATEGORIES).map(([key, meta]) => (
            <option key={key} value={key}>
              {meta.label} ({num(byFlag[key] ?? 0)})
            </option>
          ))}
        </select>
        {flag && CATEGORIES[flag] && (
          <span className="facet-note">
            {CATEGORIES[flag].excluded ? 'не влиза в общите суми' : 'влиза в общите суми'}
          </span>
        )}
      </div>

      {rows.isError && <ErrorState message={(rows.error).message} />}
      {rows.isPending ? <Loading /> : (
        <section className="block">
          <SectionHead title="Разминаване между прогноза и договор"
            aside="подредени по договорена стойност" />
          <DataTable
            rows={rows.data?.valueObservations ?? []}
            rowKey={(row) => row.tenderId}
            empty="Няма поръчки в тази категория."
            columns={[
              {
                key: 'tender',
                label: 'Поръчка',
                render: (row) => (
                  <div>
                    <Link href={`#/tenders/${encodeURIComponent(row.tenderId)}`}>
                      {truncate(row.title ?? row.tenderId, 60)}
                    </Link>
                    <div className="uic mono">
                      {row.tenderId}
                      {row.buyer && ` · ${truncate(row.buyer, 34)}`}
                      {row.publicationDate && ` · ${date(row.publicationDate)}`}
                    </div>
                  </div>
                ),
              },
              {
                key: 'estimated',
                label: 'Прогнозна',
                num: true,
                render: (row) => <Amount value={row.estimatedValue} currency={row.currency} />,
              },
              {
                key: 'awarded',
                label: 'Договорена',
                num: true,
                render: (row) => (
                  <Amount value={row.awardedAmount} currency={row.currency} />
                ),
              },
              {
                key: 'ratio',
                label: 'Съотношение',
                num: true,
                render: (row) => (row.ratio == null
                  ? '—'
                  : (
                    <span className={row.excluded ? 'debt' : 'amount'}>
                      {row.ratio >= 10 ? `${row.ratio.toFixed(0)}×` : `${(row.ratio * 100).toFixed(1)}%`}
                    </span>
                  )),
              },
              {
                key: 'flag',
                label: 'Категория',
                render: (row) => (
                  <span>
                    {CATEGORIES[row.flag]?.label ?? row.flag}
                    {row.excluded && (
                      <>
                        {' '}
                        <Badge kind="debt">извън сумите</Badge>
                      </>
                    )}
                    {(row.lotCount ?? 0) > 1 && (
                      <div className="uic mono">{row.lotCount} обособени позиции</div>
                    )}
                  </span>
                ),
              },
            ]}
          />
          <Pager page={page} pageSize={PAGE_SIZE} total={total} onGo={setPage} />
        </section>
      )}

      <section className="block">
        <SectionHead title="Какво значи всяка категория" aside="определения" />
        <dl className="detail-meta detail-meta-wide">
          {Object.entries(CATEGORIES).map(([key, meta]) => (
            <div key={key}>
              <dt>
                {meta.label}
                {meta.excluded ? ' · извън сумите' : ' · влиза в сумите'}
              </dt>
              <dd>{meta.note}</dd>
            </div>
          ))}
        </dl>
      </section>

      {/* Records that could not be used at all — a reader who wonders why a
          company is absent from a dossier should find the reason here
          instead of concluding there is none. */}
      {issues.data && issues.data.dataIssues.length > 0 && (
        <section className="block">
          <SectionHead title="Неизползваеми записи"
            aside={`${num(issueTotal)} общо · последните ${issues.data.dataIssues.length}`} />
          <DataTable
            rows={issues.data.dataIssues}
            rowKey={(row, index) => `${row.source}:${row.recordId}:${index}`}
            columns={[
              { key: 'source', label: 'Източник', render: (row) => row.source },
              {
                key: 'record',
                label: 'Запис',
                render: (row) => (
                  <span className="mono">{truncate(row.recordId ?? '—', 34)}</span>
                ),
              },
              {
                key: 'value',
                label: 'Стойност',
                render: (row) => (
                  <span className="mono">{truncate(row.value ?? '—', 44)}</span>
                ),
              },
              { key: 'reason', label: 'Причина', render: (row) => truncate(row.reason, 70) },
            ]}
          />
        </section>
      )}

      <p className="colophon-note">
        Разминаването се открива само когато и двете числа са публикувани — поръчка без
        прогнозна стойност не може да бъде проверена по този начин и стойността ѝ се брои.
        Поръчка с няколко изпълнителя се брои изцяло за всеки от тях, защото източникът не
        публикува разпределение между тях.
      </p>
    </>
  );
}
